/**
 * Translation engine settings for a flow (`flow.translations`), sent with
 * `PUT /captions/:id` as one complete map:
 *
 *   { [lang]: { engine: 'gemini', formality?, compress?, genderAware?, instructions? } }
 *
 * Rules (the captions API brief):
 *   - keys are translated tracks only: every `langs` entry except `source`
 *     (core answers 400 for any other key), so the map is always rebuilt from
 *     the current draft, never copied from what was saved;
 *   - only languages on Gemini get an entry, always `engine: 'gemini'`.
 *     Speechmatics is the default: a bare `{ engine: 'speechmatics' }` entry
 *     is dropped by core on English pairs and means "don't translate" on a
 *     non-English pair, so none is ever created; "off" is the empty map;
 *   - with `nonEnglishPairs`, a pair Speechmatics can't translate (no English
 *     side, or a captions-only side) must be on Gemini or core answers 400:
 *     those languages get a Gemini entry even while the switch is off;
 *   - options are omitted while at their defaults: formality 'default',
 *     compress on (sent only as `false`), genderAware off, no instructions;
 *   - genderAware needs the recognition `speakers` option on;
 *   - `model` is never sent.
 *
 * `status.production.translation[lang]` reports what the runner is doing per
 * language; `translationWarning` turns it into publisher copy (no latency
 * numbers). Translated tracks are sentence-timed on both engines.
 */

export const INSTRUCTIONS_MAX = 500;
export const FORMALITY_OPTIONS = Object.freeze([
	{ value: 'default', text: 'Default' },
	{ value: 'formal', text: 'Formal' },
	{ value: 'informal', text: 'Informal' },
]);
export const GENDER_AWARE_NEEDS_SPEAKERS = 'Turn on Speaker labels to use this.';
export const COMPRESS_HELP = 'Condenses long sentences so captions stay readable.';
export const GENDER_AWARE_HELP = "Uses each speaker's gender where the language marks it.";
export const GEMINI_HELP = 'Sentence-at-a-time; often reads better.';
export const FALLBACK_WARNING = 'Gemini unavailable, using Speechmatics';
export const ENGINE_NONE_WARNING = "This pair can't be translated by Speechmatics. Turn on Translate with Gemini.";
import { FALLBACK_CATALOG, needsGemini } from './captionLanguages';

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

/** The translated tracks of a draft: `langs` without the source. */
export function translatedLangs(source, langs) {
	return (Array.isArray(langs) ? langs : []).filter((l) => !source || !same(l, source));
}

export function defaultOptions() {
	return { formality: 'default', compress: true, genderAware: false, instructions: '' };
}

/** Translated languages that must be on Gemini whatever the switch says. */
export function requiredGeminiLangs({ source, langs, catalog = FALLBACK_CATALOG } = {}) {
	return translatedLangs(source, langs).filter((l) => needsGemini(source, l, catalog));
}

/** Translated languages that get a Gemini entry: all with the switch on, else the required ones. */
export function geminiLangs(draft, ctx = {}) {
	const translated = translatedLangs(ctx.source, ctx.langs);
	if (draft?.gemini) return translated;
	const required = requiredGeminiLangs(ctx);
	return translated.filter((l) => required.includes(l));
}

/**
 * `{ gemini, options: { [lang]: {...} } }` from a saved `flow.translations`.
 * The switch reads on when a language that could stay on Speechmatics is on
 * Gemini; entries a pair requires (see `requiredGeminiLangs`) don't count.
 */
export function draftFromTranslations(translations, { source = '', langs = [], catalog = FALLBACK_CATALOG } = {}) {
	const map = translations && typeof translations === 'object' && !Array.isArray(translations) ? translations : {};
	const required = requiredGeminiLangs({ source, langs, catalog });
	const options = {};
	let gemini = false;
	for (const [lang, entry] of Object.entries(map)) {
		if (!entry || typeof entry !== 'object') continue;
		if (entry.engine === 'gemini' && !required.some((r) => same(r, lang))) gemini = true;
		options[lang] = {
			formality: ['formal', 'informal'].includes(entry.formality) ? entry.formality : 'default',
			compress: entry.compress !== false,
			genderAware: entry.genderAware === true,
			instructions: typeof entry.instructions === 'string' ? entry.instructions : '',
		};
	}
	return { gemini, options };
}

/** Options for one language, defaults filled in. */
export function optionsFor(draft, lang) {
	const key = Object.keys(draft?.options || {}).find((k) => same(k, lang));
	return { ...defaultOptions(), ...(key ? draft.options[key] : {}) };
}

/**
 * The `translations` map to send: one Gemini entry per language in
 * `geminiLangs`, defaults omitted, keys limited to the current translated langs.
 */
export function buildTranslations(draft, { source, langs, speakers = false, catalog = FALLBACK_CATALOG } = {}) {
	const out = {};
	for (const lang of geminiLangs(draft, { source, langs, catalog })) {
		const o = optionsFor(draft, lang);
		const entry = { engine: 'gemini' };
		if (o.formality === 'formal' || o.formality === 'informal') entry.formality = o.formality;
		if (o.compress === false) entry.compress = false;
		if (o.genderAware && speakers) entry.genderAware = true;
		const instructions = String(o.instructions || '').trim();
		if (instructions) entry.instructions = instructions;
		out[lang] = entry;
	}
	return out;
}

/** Order-independent comparison key for a translations map. */
export function translationsKey(map) {
	const m = map && typeof map === 'object' ? map : {};
	return JSON.stringify(Object.keys(m).sort().map((lang) => [lang, Object.keys(m[lang] || {}).sort().map((k) => [k, m[lang][k]])]));
}

/** '' or why an instructions value can't be saved. */
export function instructionsError(value) {
	const text = String(value || '');
	if (text.length > INSTRUCTIONS_MAX) return `Keep instructions to ${INSTRUCTIONS_MAX} characters or fewer.`;
	if (CONTROL_CHARS.test(text)) return "Instructions can't contain line breaks or control characters.";
	return '';
}

/** Client-side errors per Gemini language: `{ [lang]: { instructions } }`. */
export function validateTranslations(draft, ctx = {}) {
	const errors = {};
	for (const lang of geminiLangs(draft, ctx)) {
		const msg = instructionsError(optionsFor(draft, lang).instructions);
		if (msg) errors[lang] = { instructions: msg };
	}
	return errors;
}

const SERVER_FIELDS = ['formality', 'compress', 'genderAware', 'instructions'];

/**
 * `{ [lang]: { [field]: { message, value } } }` from a 400
 * `captions_invalid_translations`, each kept with the value it was raised on
 * (from `valueOf(lang, field)`) so editing the field clears it. Returns
 * `{ mapped, general }`; `general` holds messages with no language.
 */
export function mapServerTranslationErrors(errors, valueOf) {
	const mapped = {};
	const general = [];
	for (const e of Array.isArray(errors) ? errors : []) {
		const message = String(e?.message || 'Invalid value.');
		const where = locateTranslationError(e?.path);
		if (!where) {
			general.push(message);
			continue;
		}
		const field = SERVER_FIELDS.includes(where.field) ? where.field : 'general';
		mapped[where.lang] = mapped[where.lang] || {};
		mapped[where.lang][field] = { message, value: valueOf(where.lang, field) };
	}
	return { mapped, general };
}

/**
 * Field errors for a draft: client validation plus the server's, minus server
 * errors whose field has been edited since. `{ [lang]: { [field]: message } }`.
 */
export function translationErrorsFor(draft, ctx = {}, serverErrors = {}) {
	const out = validateTranslations(draft, ctx);
	const translated = translatedLangs(ctx.source, ctx.langs);
	for (const [lang, fields] of Object.entries(serverErrors || {})) {
		if (!translated.some((l) => same(l, lang))) continue;
		for (const [field, err] of Object.entries(fields || {})) {
			if (field !== 'general' && optionsFor(draft, lang)[field] !== err.value) continue;
			out[lang] = out[lang] || {};
			if (!out[lang][field]) out[lang][field] = err.message;
		}
	}
	return out;
}

/** Options that differ from the defaults; gender-aware counts only with Speaker labels on. */
export function isCustomised(options, speakers = false) {
	const o = { ...defaultOptions(), ...(options || {}) };
	return o.formality !== 'default' || o.compress === false || (o.genderAware && speakers) || !!String(o.instructions || '').trim();
}

/** "Formal · Shorten off · Instructions", or "Default settings". */
export function optionsSummary(options, speakers = false) {
	const o = { ...defaultOptions(), ...(options || {}) };
	const parts = [];
	if (o.formality === 'formal') parts.push('Formal');
	if (o.formality === 'informal') parts.push('Informal');
	if (o.compress === false) parts.push('Shorten off');
	if (o.genderAware && speakers) parts.push('Gender-aware');
	if (String(o.instructions || '').trim()) parts.push('Instructions');
	return parts.length ? parts.join(' · ') : 'Default settings';
}

/** Saved languages whose translation is gender-aware. */
export function genderAwareLangs(translations) {
	const map = translations && typeof translations === 'object' ? translations : {};
	return Object.keys(map).filter((lang) => map[lang]?.genderAware === true);
}

/** `translations.fr.instructions` -> { lang: 'fr', field: 'instructions' }; null otherwise. */
export function locateTranslationError(path) {
	const parts = Array.isArray(path) ? path.map(String) : String(path || '').split('.');
	if (parts[0] !== 'translations' || !parts[1]) return null;
	return { lang: parts[1], field: parts[2] || '' };
}

/**
 * Soft warning for a language from `status.production.translation[lang]`:
 * `{ key: 'fallback' | 'none', text }` or null.
 */
export function translationWarning(lang, status) {
	const map = status?.production?.translation;
	if (!map || typeof map !== 'object') return null;
	const key = Object.keys(map).find((k) => same(k, lang));
	const info = key ? map[key] : null;
	if (!info || typeof info !== 'object') return null;
	if (info.fallback === true) return { key: 'fallback', text: FALLBACK_WARNING };
	if (info.engine === 'none') return { key: 'none', text: ENGINE_NONE_WARNING };
	return null;
}
